import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Box, List, Drawer, Collapse, ListItemText, ListItemIcon, ListItemButton, Stack } from '@mui/material';
// config
import { NAVBAR } from '../../config';
// components
import Logo from '../../components/Logo';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import { IconButtonAnimate } from '../../components/animate';
import { NavSectionVertical } from '../../components/nav-section';
import { useAppContext } from '../../contexts/AppContext';
import { APP_STATE } from '../../constants/app_state';

// ----------------------------------------------------------------------

const ListItemStyle = styled(ListItemButton)(({ theme }) => ({
  ...theme.typography.body2,
  height: NAVBAR.DASHBOARD_ITEM_ROOT_HEIGHT,
  textTransform: 'capitalize',
  fontWeight: 'bold',
  color: theme.palette.text.secondary,
}));

// ----------------------------------------------------------------------

MenuMobile.propTypes = {
  navConfig: PropTypes.array,
};

export default function MenuMobile({ navConfig }) {
  const { pathname } = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (drawerOpen) {
      handleDrawerClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleDrawerOpen}
        sx={{
          ml: 1,
          mt: 2,
          color: 'common.white',
        }}
      >
        <Iconify icon={'eva:menu-2-fill'} sx={{ height: 40, width: 40 }} />
      </IconButtonAnimate>

      <Drawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { pb: 5, width: 330 } }}
      >
        <Scrollbar>
          <Logo sx={{ mx: 2.5, my: 3, width: 120, height: 80 }} />

          <List disablePadding>
            {navConfig.map((link) => (
              <MenuMobileItem key={link.title} item={link} />
            ))}
          </List>

          <Stack width={1} direction="row" justifyContent="start" p={3}>
            <Box
              id="google_translate_element"
              sx={{
                width: 1,
                minHeight: 40,
                '& .goog-te-gadget': { fontSize: 0, color: 'transparent' },
                '& .goog-te-gadget span, & .goog-logo-link': { display: 'none' },
                '& .goog-te-combo': {
                  width: '100%',
                  height: 40,
                  m: 0,
                  px: 1.5,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: 0,
                  bgcolor: 'common.white',
                  color: 'text.primary',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  outline: 'none',
                },
              }}
            />
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}

// ----------------------------------------------------------------------

MenuMobileItem.propTypes = {
  item: PropTypes.shape({
    children: PropTypes.array,
    icon: PropTypes.any,
    path: PropTypes.string,
    title: PropTypes.string,
  }),
};

function MenuMobileItem({ item }) {
  const { title, path, icon, children, action } = item;
  const [open, setOpen] = useState(false);
  const { appdispatch, handleOpen: handleNewsletterOpen } = useAppContext();

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleSelected = () => {
    if (action === 'newsletter') {
      handleNewsletterOpen();
      return;
    }

    handleOpen();
    appdispatch({
      type: APP_STATE.subMenu,
      payload: children?.at(0)?.items || [],
    });
  };

  if (children) {
    return (
      <>
        <ListItemStyle onClick={handleSelected}>
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText disableTypography primary={title} />
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column-reverse' }}>
            <NavSectionVertical navConfig={children} />
          </Box>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      {...(path && { to: path, end: path === '/' })}
      component={path ? RouterLink : 'button'}
      onClick={handleSelected}
      sx={{
        ...(!path && {
          width: 1,
          border: 0,
          textAlign: 'left',
          cursor: 'pointer',
        }),
        '&.active': {
          color: 'primary.main',
          fontWeight: 'fontWeightMedium',
          bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
        },
      }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText disableTypography primary={title} />
    </ListItemStyle>
  );
}
